import { MinusCircleIcon, PlusCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { SystemScoreInlineType } from 'data/types';
import { BONUS_LABELS, PENALTY_LABELS } from './score-breakdown-labels';

type ScorePenaltiesBonusesProps = Readonly<{
  score: SystemScoreInlineType | null | undefined;
}>;

function BreakdownItem({
  label,
  value,
  kind
}: {
  label: string;
  value: number;
  kind: 'penalty' | 'bonus';
}) {
  const active = value !== 0;
  const isPenalty = kind === 'penalty';

  return (
    <div
      className={clsx(
        'flex items-center justify-between rounded px-2 py-1 text-xs transition-opacity',
        active ? 'opacity-100' : 'opacity-30'
      )}
    >
      <div className='flex items-center gap-1.5'>
        {isPenalty ? (
          <MinusCircleIcon
            className={clsx(
              'h-3.5 w-3.5 shrink-0',
              active ? 'text-red-500' : 'text-gray-400'
            )}
          />
        ) : (
          <PlusCircleIcon
            className={clsx(
              'h-3.5 w-3.5 shrink-0',
              active ? 'text-green-500' : 'text-gray-400'
            )}
          />
        )}
        <span className='text-gray-600'>{label}</span>
      </div>
      <span
        className={clsx(
          'font-semibold tabular-nums',
          !active && 'text-gray-400',
          active && isPenalty && 'text-red-600',
          active && !isPenalty && 'text-green-600'
        )}
      >
        {isPenalty && value !== 0 ? '−' : ''}
        {isPenalty ? value : `+${value}`}
      </span>
    </div>
  );
}

export default function ScorePenaltiesBonuses({
  score
}: ScorePenaltiesBonusesProps) {
  if (!score) return null;

  const { breakdown, totalPenalty, totalBonus } = score.penaltiesBonuses;

  return (
    <div className='grid grid-cols-2 gap-4'>
      {/* Penalties column */}
      <div className='rounded-lg border border-gray-200 bg-white p-3'>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
            Penalties
          </span>
          <span
            className={clsx(
              'text-xs font-bold tabular-nums',
              totalPenalty > 0 ? 'text-red-600' : 'text-gray-400'
            )}
          >
            {totalPenalty > 0 ? `−${totalPenalty.toFixed(1)}` : '0'}
          </span>
        </div>
        <div className='flex flex-col gap-0.5'>
          {PENALTY_LABELS.map(({ code, label }) => (
            <BreakdownItem
              key={code}
              label={label}
              value={Math.round((breakdown[code] ?? 0) * 10) / 10}
              kind='penalty'
            />
          ))}
        </div>
      </div>

      {/* Bonuses column */}
      <div className='rounded-lg border border-gray-200 bg-white p-3'>
        <div className='mb-2 flex items-center justify-between'>
          <span className='text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
            Bonuses
          </span>
          <span
            className={clsx(
              'text-xs font-bold tabular-nums',
              totalBonus > 0 ? 'text-green-600' : 'text-gray-400'
            )}
          >
            {totalBonus > 0 ? `+${totalBonus.toFixed(1)}` : '0'}
          </span>
        </div>
        <div className='flex flex-col gap-0.5'>
          {BONUS_LABELS.map(({ code, label }) => (
            <BreakdownItem
              key={code}
              label={label}
              value={Math.round((breakdown[code] ?? 0) * 10) / 10}
              kind='bonus'
            />
          ))}
        </div>
      </div>
    </div>
  );
}
